import React, {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  computeMarketCalculatedData,
  EMPTY_CALCULATED_DATA,
  type ICalculatedData,
} from "~/feature/event/utils/computeMarketCalculatedData";
import type { IMarket } from "~/types/event";

export type { ICalculatedData } from "~/feature/event/utils/computeMarketCalculatedData";

interface IMarketCalculatorContext {
  market: IMarket | null;
  groupedMarkets: IMarket[];
  answer: boolean;
  betAmount: number;
  calculatedData: ICalculatedData;
  calculatedGroupedData: ICalculatedData[];
  isCalculatorVisible: boolean;
}

interface IMarketCalculatorActionsContext {
  setMarket: (market: IMarket | null) => void;
  setGroupedMarkets: (markets: IMarket[]) => void;
  setAnswer: (answer: boolean) => void;
  setBetAmount: (amount: number) => void;
  setIsCalculatorVisible: (visible: boolean) => void;
}

const MarketCalculatorContext = createContext<IMarketCalculatorContext>({
  market: null,
  groupedMarkets: [],
  answer: true,
  betAmount: 0,
  calculatedData: EMPTY_CALCULATED_DATA,
  calculatedGroupedData: [],
  isCalculatorVisible: true,
});

const MarketCalculatorActionsContext =
  createContext<IMarketCalculatorActionsContext>({
    setMarket: () => {},
    setGroupedMarkets: () => {},
    setAnswer: () => {},
    setBetAmount: () => {},
    setIsCalculatorVisible: () => {},
  });

interface IMarketCalculatorProviderProps {
  children: ReactNode;
  initialMarket?: IMarket | null;
}

export const MarketCalculatorProvider = ({
  children,
  initialMarket = null,
}: IMarketCalculatorProviderProps) => {
  const [market, setMarket] = useState<IMarket | null>(initialMarket);
  const [groupedMarkets, setGroupedMarkets] = useState<IMarket[]>([]);
  const [answer, setAnswer] = useState<boolean>(true);
  const [betAmount, setBetAmount] = useState<number>(100);
  const [isCalculatorVisible, setIsCalculatorVisible] = useState<boolean>(true);

  const calculatedGroupedData = useMemo(
    () =>
      groupedMarkets.map((groupedMarket) =>
        computeMarketCalculatedData(groupedMarket, answer, betAmount)
      ),
    [answer, betAmount, groupedMarkets]
  );

  const calculatedData = useMemo(() => {
    if (!market) {
      return EMPTY_CALCULATED_DATA;
    }

    return computeMarketCalculatedData(market, answer, betAmount);
  }, [answer, betAmount, market]);

  const value = useMemo(
    () => ({
      market,
      groupedMarkets,
      answer,
      betAmount,
      calculatedData,
      calculatedGroupedData,
      isCalculatorVisible,
    }),
    [
      answer,
      betAmount,
      calculatedData,
      isCalculatorVisible,
      market,
      calculatedGroupedData,
      groupedMarkets,
    ]
  );

  const actions = useMemo(
    () => ({
      setMarket,
      setGroupedMarkets,
      setAnswer,
      setBetAmount,
      setIsCalculatorVisible,
    }),
    [
      setAnswer,
      setBetAmount,
      setIsCalculatorVisible,
      setMarket,
      setGroupedMarkets,
    ]
  );

  return (
    <MarketCalculatorActionsContext.Provider value={actions}>
      <MarketCalculatorContext.Provider value={value}>
        {children}
      </MarketCalculatorContext.Provider>
    </MarketCalculatorActionsContext.Provider>
  );
};

export const useMarketCalculator = (): IMarketCalculatorContext &
  IMarketCalculatorActionsContext => {
  const context = useContext(MarketCalculatorContext);
  const actions = useContext(MarketCalculatorActionsContext);

  if (!context || !actions)
    throw new Error(
      "useMarketCalculator must be used within MarketCalculatorProvider"
    );

  return {
    ...context,
    ...actions,
  };
};

export const useMarketCalculatorActions =
  (): IMarketCalculatorActionsContext => {
    const context = useContext(MarketCalculatorActionsContext);

    if (!context)
      throw new Error(
        "useMarketCalculatorActions must be used within MarketCalculatorProvider"
      );

    return context;
  };
